import React from 'react';
import { Helmet } from 'react-helmet';
import {
    FaUserNurse,
    FaUserShield,
    FaEnvelope,
    FaPhone,
    FaWhatsapp,
    FaQuestionCircle,
    FaBook,
    FaClock,
    FaCheckCircle,
    FaMobileAlt,
    FaLaptop,
} from 'react-icons/fa';
import { FaHeartPulse } from 'react-icons/fa6';
import { MdOutlineVerified, MdSupportAgent } from 'react-icons/md';
import { Link } from 'react-router-dom';

const HelpPage = () => {
    const url = window.location.href;

    const faqs = {
        patients: [
            {
                question: 'How do I get started with HealthConnect?',
                answer: 'Download the HealthConnect mobile app, create your profile, and add your health conditions. Our system will match you with qualified nurses in your area.'
            },
            {
                question: 'How are nurses verified?',
                answer: 'All nurses undergo rigorous background checks, license verification with the Nursing Council of Kenya, and credential validation before joining our platform.'
            },
            {
                question: 'Can I choose my nurse?',
                answer: 'Yes! Our algorithm matches you based on your conditions and location, but you can review nurse profiles and request specific nurses for future bookings.'
            },
            {
                question: 'What if I need emergency care?',
                answer: 'HealthConnect is NOT for emergencies. For medical emergencies, call 999 or visit your nearest hospital immediately. Our services are for non-urgent home healthcare.'
            },
            {
                question: 'How do I pay for services?',
                answer: 'We accept M-Pesa and credit/debit cards. Payment is required before service commencement unless otherwise arranged.'
            },
            {
                question: 'Can I cancel a booking?',
                answer: 'Yes! Free cancellation 24+ hours before. 50% fee for 12-24 hours notice. Full charge for cancellations less than 12 hours before scheduled time.'
            }
        ],
        nurses: [
            {
                question: 'How do I register as a nurse?',
                answer: 'Click "Register as Nurse" on our website, complete your profile, upload your nursing license, ID, and certificates. Our admin team will verify your documents within 3-5 business days.'
            },
            {
                question: 'What documents do I need?',
                answer: 'Valid nursing license from Nursing Council of Kenya, National ID, professional certificates, proof of liability insurance, and passport-size photo.'
            },
            {
                question: 'How do I get paid?',
                answer: 'Payments are processed weekly via M-Pesa or bank transfer. You can view your earnings, completed jobs, and payment history in your nurse dashboard.'
            },
            {
                question: 'Can I choose which jobs to accept?',
                answer: 'Yes! You have full control over your availability. Browse available jobs in your area and accept those that fit your schedule and expertise.'
            },
            {
                question: 'What support do you provide nurses?',
                answer: 'We provide onboarding training, 24/7 support, professional development resources, and a community of healthcare professionals to connect with.'
            },
            {
                question: 'Is there a fee to join?',
                answer: 'Registration is free. We charge a small commission on completed bookings. No hidden fees or monthly subscriptions.'
            }
        ],
        admin: [
            {
                question: 'How do I verify a nurse?',
                answer: 'Access the admin dashboard, navigate to Nurses > Pending Verification. Review uploaded documents, cross-check with Nursing Council records, and approve or reject with reasons.'
            },
            {
                question: 'How do I assign nurses to patients?',
                answer: 'Go to Bookings > Pending Assignment. Review patient conditions and location, then manually assign a qualified nurse or let the algorithm auto-match.'
            },
            {
                question: 'How do I handle disputes?',
                answer: 'Access Reports & Disputes in the admin dashboard. Review both parties accounts, communicate via the platform, and issue refunds or warnings as appropriate.'
            },
            {
                question: 'Can I view analytics?',
                answer: 'Yes! The admin dashboard provides comprehensive analytics on bookings, revenue, nurse performance, patient satisfaction, and growth metrics.'
            }
        ]
    };

    return (
        <>
            <Helmet>
                <title>Help & Support - HealthConnect</title>
                <link rel="canonical" href={url} />
                <meta
                    name="description"
                    content="HealthConnect Help Center. Find answers to common questions about our home healthcare services, nurse registration, bookings, payments, and support."
                />
                <meta
                    property="og:title"
                    content="Help & Support - HealthConnect"
                />
                <meta
                    property="og:description"
                    content="Get help with HealthConnect services. FAQs, guides, and support for patients, nurses, and admin users."
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />
                <meta
                    name="keywords"
                    content="help center, support, FAQs, HealthConnect Kenya, nurse booking help, patient support"
                />
            </Helmet>

            <div className="min-h-screen bg-base-100">
                <section className="px-6 md:px-12 py-5 bg-base-100 relative overflow-hidden -mt-4">
                    <div className="absolute -top-24 -right-24 size-96 bg-primary/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-24 -left-24 size-96 bg-blue-400/10 rounded-full blur-3xl"></div>

                    <div className="max-w-5xl mx-auto relative z-10">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                            <span className="size-2 rounded-full bg-primary mr-2 animate-pulse"></span>
                            Help Center
                        </span>

                        <h2 className="text-3xl md:text-5xl font-black text-text-main dark:text-white leading-[1.1] tracking-tight mb-8">
                            How Can We <span className="text-primary">Help You?</span>
                        </h2>

                        <p className="text-lg text-text-secondary dark:text-slate-400 font-light max-w-2xl leading-relaxed">
                            Find answers to common questions, get support, and learn how to make the most of HealthConnect services.
                        </p>
                    </div>
                </section>

                <section className="py-16 px-6 bg-base-100">
                    <div className="">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div
                                onClick={() => {
                                    const patients = document.getElementById('patients')
                                    patients.scrollIntoView({ behavior: 'smooth' })
                                }}
                                className="hover:cursor-pointer group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover group-hover:cursor-pointer hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
                                <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                    <FaMobileAlt className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">For Patients</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed mb-4">
                                    Download the app, book nurses, manage bookings, and get care at home.
                                </p>
                                <span className="text-primary font-bold text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Learn More <FaQuestionCircle className="text-xs" />
                                </span>
                            </div>

                            <div
                                onClick={() => {
                                    const patients = document.getElementById('nurses')
                                    patients.scrollIntoView({ behavior: 'smooth' })
                                }}
                                className="hover:cursor-pointer group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
                                <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                    <FaUserNurse className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">For Nurses</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed mb-4">
                                    Register, get verified, accept jobs, and grow your healthcare career.
                                </p>
                                <span className="text-primary font-bold text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Learn More <FaQuestionCircle className="text-xs" />
                                </span>
                            </div>

                            <Link to="/contact"
                                className="hover:cursor-pointer group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
                                <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                    <MdSupportAgent className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Contact Support</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed mb-4">
                                    Get in touch with our team for personalized assistance and support.
                                </p>
                                <span className="text-primary font-bold text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer">
                                    Get Help <FaEnvelope className="text-xs" />
                                </span>
                            </Link>
                        </div>
                    </div>
                </section>
                <section id="patients" className="py-20 px-6 bg-base-200/50">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                                Patient Support
                            </span>
                            <h2 className="text-3xl md:text-4xl font-black text-text-main dark:text-white mb-4">
                                For <span className="text-primary">Patients</span>
                            </h2>
                            <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                                Everything you need to know about using HealthConnect to receive care at home.
                            </p>
                        </div>

                        <div className="bg-primary rounded-2xl p-8 mb-12">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-center md:text-left">
                                    <h3 className="text-2xl font-black text-white mb-3">Download the HealthConnect App</h3>
                                    <p className="text-white/80 mb-0">Get started by downloading our mobile app. Available for iOS and Android.</p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button className="px-6 py-3 bg-white text-primary font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center gap-3">
                                        <FaMobileAlt className="text-xl" />
                                        <div className="text-left">
                                            <p className="text-xs">Download on the</p>
                                            <p className="text-sm">App Store</p>
                                        </div>
                                    </button>
                                    <button className="px-6 py-3 bg-white text-primary font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center gap-3">
                                        <FaMobileAlt className="text-xl" />
                                        <div className="text-left">
                                            <p className="text-xs">Get it on</p>
                                            <p className="text-sm">Google Play</p>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Patient FAQs */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {faqs.patients.map((faq, index) => (
                                <div key={index} className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                            <FaQuestionCircle className="text-lg" />
                                        </div>
                                        <h3 className="text-lg font-bold text-text-main dark:text-white">{faq.question}</h3>
                                    </div>
                                    <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed pl-14">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* For Nurses Section */}
                <section id="nurses" className="py-20 px-6 bg-base-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold uppercase tracking-wider mb-6">
                                Nurse Support
                            </span>
                            <h2 className="text-3xl md:text-4xl font-black text-text-main dark:text-white mb-4">
                                For <span className="text-accent">Nurses</span>
                            </h2>
                            <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                                Join our network of verified healthcare professionals and grow your career.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-6 mb-12">
                            <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-accent/30 transition-all duration-300">
                                <div className="size-14 rounded-2xl bg-accent/20 text-accent flex items-center justify-center mb-5 group-hover:scale-110 transition-transform relative">
                                    <span className="absolute -top-2 -right-2 size-6 bg-accent text-white rounded-full text-xs font-bold flex items-center justify-center">1</span>
                                    <FaUserNurse className="text-3xl" />
                                </div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white mb-3">Register</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm">Create your account on our website with basic information.</p>
                            </div>

                            <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-accent/30 transition-all duration-300">
                                <div className="size-14 rounded-2xl bg-accent/20 text-accent flex items-center justify-center mb-5 group-hover:scale-110 transition-transform relative">
                                    <span className="absolute -top-2 -right-2 size-6 bg-accent text-white rounded-full text-xs font-bold flex items-center justify-center">2</span>
                                    <FaBook className="text-3xl" />
                                </div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white mb-3">Upload Documents</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm">Submit your license, ID, and certificates for verification.</p>
                            </div>

                            <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-accent/30 transition-all duration-300">
                                <div className="size-14 rounded-2xl bg-accent/20 text-accent flex items-center justify-center mb-5 group-hover:scale-110 transition-transform relative">
                                    <span className="absolute -top-2 -right-2 size-6 bg-accent text-white rounded-full text-xs font-bold flex items-center justify-center">3</span>
                                    <MdOutlineVerified className="text-3xl" />
                                </div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white mb-3">Get Verified</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm">Admin reviews your application within 3-5 business days.</p>
                            </div>

                            <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-accent/30 transition-all duration-300">
                                <div className="size-14 rounded-2xl bg-accent/20 text-accent flex items-center justify-center mb-5 group-hover:scale-110 transition-transform relative">
                                    <span className="absolute -top-2 -right-2 size-6 bg-accent text-white rounded-full text-xs font-bold flex items-center justify-center">4</span>
                                    <FaCheckCircle className="text-3xl" />
                                </div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white mb-3">Start Working</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm">Accept jobs, provide care, and get paid weekly.</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {faqs.nurses.map((faq, index) => (
                                <div key={index} className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-accent/30 transition-all duration-300">
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="size-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                                            <FaQuestionCircle className="text-lg" />
                                        </div>
                                        <h3 className="text-lg font-bold text-text-main dark:text-white">{faq.question}</h3>
                                    </div>
                                    <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed pl-14">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="text-center mt-12">
                            <Link
                                to="/about#for-nurses"
                                className="inline-flex items-center gap-3 h-14 px-10 bg-accent text-white font-bold text-lg rounded-xl hover:bg-accent/90 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 group"
                            >
                                Register as Nurse
                                <FaQuestionCircle className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </section>

                <section id="admin" className="py-20 px-6 bg-base-200/50">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-neutral/10 text-neutral dark:text-white text-xs font-bold uppercase tracking-wider mb-6">
                                Admin Support
                            </span>
                            <h2 className="text-3xl md:text-4xl font-black text-text-main dark:text-white mb-4">
                                For <span className="text-neutral dark:text-primary">Administrators</span>
                            </h2>
                            <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                                Manage your platform, verify nurses, and oversee operations efficiently.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 mb-12">
                            {faqs.admin.map((faq, index) => (
                                <div key={index} className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-neutral/30 transition-all duration-300">
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="size-10 rounded-lg bg-neutral/10 dark:bg-primary/10 text-neutral dark:text-primary flex items-center justify-center flex-shrink-0">
                                            <FaUserShield className="text-lg" />
                                        </div>
                                        <h3 className="text-lg font-bold text-text-main dark:text-white">{faq.question}</h3>
                                    </div>
                                    <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed pl-14">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                                <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                    <FaLaptop className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Admin Dashboard</h3>
                                <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed mb-4">
                                    Access your admin panel to manage users, bookings, and platform settings.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="contact" className="py-20 px-6 bg-base-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                                Get in Touch
                            </span>
                            <h2 className="text-3xl md:text-4xl font-black text-text-main dark:text-white mb-4">
                                Contact <span className="text-primary">Support</span>
                            </h2>
                            <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                                Can't find what you're looking for? Our team is here to help.
                            </p>
                        </div>

                        <div className="bg-primary/5 dark:bg-primary/10 rounded-2xl p-6 mb-12 border border-primary/20">
                            <div className="flex items-center gap-4">
                                <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center flex-shrink-0">
                                    <FaClock className="text-3xl" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-1">Support Hours</h3>
                                    <p className="text-secondary dark:text-slate-400">
                                        Monday - Saturday: 8:00 AM - 8:00 PM EAT | Sunday: 10:00 AM - 6:00 PM EAT
                                    </p>
                                    <p className="text-sm text-primary font-bold mt-2">
                                        <FaCheckCircle className="inline mr-2" />
                                        Emergency support available 24/7 for active bookings
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <a href="mailto:support@healthconnect.co.ke" className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 text-center">
                                <div className="size-16 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                                    <FaEnvelope className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Email Us</h3>
                                <p className="text-primary font-bold text-lg">support@healthconnect.co.ke</p>
                                <p className="text-secondary dark:text-slate-400 text-sm mt-2">Response within 24 hours</p>
                            </a>

                            <a href="tel:+254700000000" className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 text-center">
                                <div className="size-16 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                                    <FaPhone className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Call Us</h3>
                                <p className="text-primary font-bold text-lg">+254 700 000 000</p>
                                <p className="text-secondary dark:text-slate-400 text-sm mt-2">During support hours</p>
                            </a>

                            <a href="https://wa.me/254700000000" target="_blank" rel="noopener noreferrer" className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-success/30 transition-all duration-300 hover:-translate-y-1 text-center">
                                <div className="size-16 rounded-2xl bg-success/20 text-success flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                                    <FaWhatsapp className="text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">WhatsApp</h3>
                                <p className="text-success font-bold text-lg">+254 700 000 000</p>
                                <p className="text-secondary dark:text-slate-400 text-sm mt-2">Quick responses</p>
                            </a>
                        </div>

                        <div className="mt-12 text-center">
                            <p className="text-secondary dark:text-slate-400 mb-6">
                                Or visit our other resources:
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link to="/privacy" className="px-6 py-3 rounded-xl bg-base-200 text-text-main dark:text-white font-bold hover:bg-primary/10 hover:text-primary transition-all">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms" className="px-6 py-3 rounded-xl bg-base-200 text-text-main dark:text-white font-bold hover:bg-primary/10 hover:text-primary transition-all">
                                    Terms of Service
                                </Link>
                                <Link to="/about" className="px-6 py-3 rounded-xl bg-base-200 text-text-main dark:text-white font-bold hover:bg-primary/10 hover:text-primary transition-all">
                                    About Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-12 px-6">
                    <div className="">
                        <div className="bg-primary rounded-[2rem] p-12 md:p-16 relative overflow-hidden shadow-2xl">
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15)_0%,transparent_70%)]"></div>

                            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
                                <div className="text-center lg:text-left max-w-2xl">
                                    <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                                        Still Need <br />Help?
                                    </h2>
                                    <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto lg:mx-0">
                                        Our support team is ready to assist you with any questions or concerns. We're here to help!
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start">
                                        <a
                                            href="mailto:support@healthconnect.co.ke"
                                            className="h-14 px-10 bg-white text-primary font-bold text-lg rounded-xl hover:bg-slate-50 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-2 group"
                                        >
                                            Email Support
                                            <FaEnvelope className="group-hover:translate-x-1 transition-transform" />
                                        </a>
                                        <a
                                            href="https://wa.me/254700000000"
                                            className="h-14 px-10 bg-primary/20 backdrop-blur-sm border-2 border-white/30 text-white font-bold text-lg rounded-xl hover:bg-primary/30 transition-all flex items-center justify-center"
                                        >
                                            WhatsApp Us
                                        </a>
                                    </div>
                                </div>

                                <div className="relative flex-shrink-0">
                                    <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl" />
                                    <div className="relative size-32 md:size-48 lg:size-64 rounded-full border-[8px] md:border-[12px] lg:border-[16px] border-white/10 flex items-center justify-center mx-auto lg:mx-0">
                                        <FaHeartPulse className="text-4xl md:text-6xl lg:text-[100px] text-white/20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default HelpPage;